// Course type definitions matching backend API

export enum CourseStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'ACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export enum CourseMethod {
  VIDEO = 'VIDEO',
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
  HYBRID = 'HYBRID'
}

export enum DurationRange {
  SHORT = 'short',    // < 10 hours
  MEDIUM = 'medium',  // 10-50 hours
  LONG = 'long'       // > 50 hours
}

export enum SortOption {
  ALPHABETICAL = 'alphabetical',
  DURATION = 'duration',
  NEWEST = 'newest'
}

export interface PublicCourse {
  id: number;
  hashId: string;
  code: string;
  name: string;
  shortDescription: string;
  detailIntroduce: string;
  rule?: string;
  content: string;
  lecturer: string;
  requiredInput: string;
  suitableObject: string;
  status: string;
  method: string;
  timeInHour: number;
  timeInLesson: number;
  totalRegisteredStudent: number;
  fee: number;
  imageUrl: string ;
  slug?: string;
  metadescription?: string;
  openGraphImageUrl?: string;
  tagNames?: string[];
  audiences?: CourseAudienceRef[];
  landingSections?: LandingSections | null;
  createdAt: Date;
  updatedAt: Date;
  upcomingClasses?: PublicClassroom[];
}

export interface CourseAudienceRef {
  id: number;
  code: string;
  name: string;
  isPrimary: boolean;
}

export interface Audience {
  id: number;
  code: string;
  name: string;
  shortQuestion?: string | null;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface LandingSections {
  heroHeadline?: string;
  heroSubheadline?: string;
  painPoints?: string[];
  outcomes?: { title: string; detail: string }[];
  beforeAfter?: { task: string; before: string; after: string; saving: string }[];
  demoUrls?: string[];
  faqs?: { q: string; a: string }[];
  instructor?: {
    imageUrl?: string;
    imageOverlay?: string;
    sectionLabel?: string;
    name?: string;
    bio?: string;
    stats?: { value: string; label: string }[];
  };
}

export interface PublicClassroom {
  id: number;
  code: string;
  name: string;
  courseId: number;
  startDate?: Date;
  endDate?: Date;
  startEnrollmentDate?: Date;
  fee?: number;
  totalSlot?: number;
  totalRegistered?: number;
  status: string;
  studyTime?: string;
  schedule?: string;
  address?: string;
  googleMeetLink?: string;
}

export interface SearchFilters {
  search?: string;
  method?: CourseMethod | CourseMethod[];
  duration?: DurationRange | DurationRange[];
  sort?: SortOption;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  audience?: string;
}

export interface SearchHighlight {
  field: 'title' | 'description';
  matches: {
    text: string;
    start: number;
    end: number;
  }[];
}

export interface PublicCourseWithHighlights extends PublicCourse {
  searchHighlights?: SearchHighlight[];
}

export interface CoursesResponse {
  success: boolean;
  message?: string;
  data: PublicCourse[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  errors?: string[];
  timestamp?: string;
  requestId?: string;
}

export interface CourseContent {
  id: number;
  hashId?: string;
  courseId: number;
  contentType: string;
  name: string;
  detail?: string; // Only available for public content
  creatorId?: number;
  objectId?: number;
  parentId?: number;
  position: number;
  status: string;
  isPublic: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface CourseSessionPublic {
  id: number;
  orderIndex: number;
  title: string;
  content?: string | null;
  output?: string | null;
}

export interface CourseDetailResponse {
  success: boolean;
  message: string;
  data: PublicCourse & {
    upcomingClasses?: PublicClassroom[];
    courseContent?: CourseContent[];
    contentOverview?: CourseContent[];
    courseSessions?: CourseSessionPublic[];
  };
  errors: string[];
  timestamp: string;
  requestId: string;
}